import { useEffect, useRef, useState } from 'react'
import { Play, Pause, SpeakerHigh, SpeakerX, GearSix, PictureInPicture, CornersOut } from '@phosphor-icons/react'

const RATES = [0.5, 0.75, 1, 1.25, 1.5, 2]

function formatTime(seconds) {
    if (!Number.isFinite(seconds)) return '0:00'
    const m = Math.floor(seconds / 60)
    const s = Math.floor(seconds % 60)
    return `${m}:${s.toString().padStart(2, '0')}`
}

function GlassButton({ onClick, title, children }) {
    return (
        <button className="player-button" title={title} onClick={onClick}>
            {children}
        </button>
    )
}

// Всплывающие панельки (скорость, качество, громкость) открываются наведением,
// а не кликом. Клик при этом тоже переключает — на тач-экране никакого hover
// нет, и без него кнопка стала бы просто неработающей.
function usePopover() {
    const [open, setOpen] = useState(false)
    const wrapRef = useRef(null)

    // Закрываем по клику снаружи — простой паттерн без сторонних библиотек:
    // слушаем клики на всём document, пока панелька открыта.
    useEffect(() => {
        if (!open) return
        const onDocClick = (e) => {
            if (!wrapRef.current?.contains(e.target)) setOpen(false)
        }
        document.addEventListener('pointerdown', onDocClick)
        return () => document.removeEventListener('pointerdown', onDocClick)
    }, [open])

    const hoverProps = {
        onMouseEnter: () => setOpen(true),
        onMouseLeave: () => setOpen(false),
    }

    // Там, где hover есть, панелька уже открыта к моменту клика — и клик по
    // кнопке не должен её схлопывать. Где hover нет (телефон), клик остаётся
    // единственным способом открыть.
    const toggleByClick = () => {
        if (window.matchMedia?.('(hover: hover)').matches) return
        setOpen((v) => !v)
    }

    return { open, setOpen, wrapRef, hoverProps, toggleByClick }
}

function SpeedControl({ rate, setRate }) {
    const { open, setOpen, wrapRef, hoverProps, toggleByClick } = usePopover()

    return (
        <div className="player-speed" ref={wrapRef} {...hoverProps}>
            <GlassButton title="Скорость воспроизведения" onClick={toggleByClick}>
                <GearSix className="player-gear-icon" size={16} weight="bold" />
            </GlassButton>
            <div className={`player-speed-menu${open ? ' player-speed-menu--visible' : ''}`}>
                {RATES.map((r) => (
                    <button
                        key={r}
                        className={`player-speed-menu-item${r === rate ? ' player-speed-menu-item--active' : ''}`}
                        onClick={() => {
                            setRate(r)
                            setOpen(false)
                        }}
                    >
                        {r === 1 ? 'Обычная' : `${r}x`}
                    </button>
                ))}
            </div>
        </div>
    )
}

// Громкость — такая же всплывающая панелька, как у настроек: раньше ползунок
// «разъезжался» вбок прямо в панели контролов и толкал соседние кнопки.
function VolumeControl({ volume, muted, setVolume, toggleMute }) {
    const { open, wrapRef, hoverProps } = usePopover()
    const silent = muted || volume === 0

    return (
        <div className="player-volume" ref={wrapRef} {...hoverProps}>
            <GlassButton title={muted ? 'Включить звук (m)' : 'Выключить звук (m)'} onClick={toggleMute}>
                {silent ? <SpeakerX size={16} weight="fill" /> : <SpeakerHigh size={16} weight="fill" />}
            </GlassButton>
            <div className={`player-speed-menu player-volume-menu${open ? ' player-speed-menu--visible' : ''}`}>
                <span className="player-volume-value">{Math.round((silent ? 0 : volume) * 100)}</span>
                <input
                    className="player-volume-range"
                    type="range"
                    min={0}
                    max={1}
                    step={0.01}
                    value={silent ? 0 : volume}
                    onChange={(e) => {
                        const next = Number(e.target.value)
                        setVolume(next)
                        // Тянуть ползунок вверх у выключенного звука и не слышать
                        // ничего — очевидная ловушка: снимаем mute сами.
                        if (muted && next > 0) toggleMute()
                    }}
                    aria-label="Громкость"
                />
            </div>
        </div>
    )
}

// Показываем, только если у видео реально есть больше одного качества (для
// обычного mp4 или совсем маленького видео qualityLevels будет пустым —
// нечего выбирать, кнопка тогда просто не рисуется).
function QualityControl({ qualityLevels, quality, activeHeight, setQuality }) {
    const { open, setOpen, wrapRef, hoverProps, toggleByClick } = usePopover()

    if (qualityLevels.length < 2) return null

    return (
        <div className="player-speed" ref={wrapRef} {...hoverProps}>
            <button
                className="player-button player-quality-button"
                title="Качество видео"
                onClick={toggleByClick}
            >
                {quality === 'auto' ? 'Авто' : `${quality}p`}
            </button>
            <div className={`player-speed-menu ${open && 'player-speed-menu--visible'}`}>
                <button
                    className={`player-speed-menu-item ${quality === 'auto' && 'player-speed-menu-item--active'}`}
                    onClick={() => { setQuality('auto'); setOpen(false) }}
                >
                    Авто{activeHeight && ` (${activeHeight}p)`}
                </button>
                {qualityLevels.map((height) => (
                    <button
                        key={height}
                        className={`player-speed-menu-item${quality === height && ' player-speed-menu-item--active'}`}
                        onClick={() => { setQuality(height); setOpen(false) }}
                    >
                        {height}p
                    </button>
                ))}
            </div>
        </div>
    )
}

// player — это весь объект, который возвращает useVideoPlayer: { state, play,
// pause, toggle, seek, ... }. Компонент только рисует UI и дёргает готовые
// методы — про video.js он ничего не знает.
//
// Разложено в два ряда (прогресс-бар отдельно от кнопок), как у YouTube — это
// не только про внешний вид: если всё пихать в один ряд, на узком вертикальном
// видео кнопкам просто не остаётся места и прогресс-бар схлопывается в ноль.
function PlayerControls({ player, visible, compact = false, enablePip = true, enableFullscreen = true, requestFullscreen, requestPip }) {
    const { state, toggle, seek, setVolume, toggleMute, setRate, setQuality } = player
    // В compact-режиме (узкий инлайн-плеер в чате) громкости/скорости/PiP просто
    // негде поместиться — прячем их, оставляя play, прогресс и fullscreen.
    const showExtras = !compact

    return (
        <div className={`player-controls${visible ? ' player-controls--visible' : ''}`}>
            <div className="player-controls-row player-controls-row__progress">
                <div className="player-progress">
                    <div className="player-progress-buffered" style={{ width: `${state.bufferedPercent * 100}%` }} />
                    <div
                        className="player-progress-played"
                        style={{ width: `${state.duration ? (state.currentTime / state.duration) * 100 : 0}%` }}
                    />
                    <input
                        className="player-seek"
                        type="range"
                        min={0}
                        max={state.duration || 0}
                        step={0.1}
                        value={state.currentTime}
                        onChange={(e) => seek(Number(e.target.value))}
                        aria-label="Перемотка"
                    />
                </div>
            </div>

            <div className="player-controls-row player-controls-row__controls">
                <GlassButton title={state.playing ? 'Пауза (space)' : 'Play (space)'} onClick={toggle}>
                    {state.playing ? <Pause size={16} weight="fill" /> : <Play size={16} weight="fill" />}
                </GlassButton>

                <span className="player-time">{formatTime(state.currentTime)} / {formatTime(state.duration)}</span>

                <div className="player-controls-spacer" />

                {showExtras && (
                    <VolumeControl
                        volume={state.volume}
                        muted={state.muted}
                        setVolume={setVolume}
                        toggleMute={toggleMute}
                    />
                )}

                {showExtras && <SpeedControl rate={state.rate} setRate={setRate} />}

                {showExtras && (
                    <QualityControl
                        qualityLevels={state.qualityLevels}
                        quality={state.quality}
                        activeHeight={state.activeHeight}
                        setQuality={setQuality}
                    />
                )}

                {enablePip && (
                    <GlassButton title="Картинка в картинке" onClick={requestPip}>
                        <PictureInPicture size={16} weight="bold" />
                    </GlassButton>
                )}

                {enableFullscreen && (
                    <GlassButton title="Полный экран (f)" onClick={requestFullscreen}>
                        <CornersOut size={16} weight="bold" />
                    </GlassButton>
                )}
            </div>
        </div>
    )
}

export default PlayerControls
